<template>
  <div
    v-if="shouldRender"
    class="ai-visibility-run-progress"
    :class="[
      `ai-visibility-run-progress--${displayVariant}`,
      { 'ai-visibility-run-progress--complete': displayIsComplete },
    ]"
  >
    <template v-if="displayVariant === 'pill'">
      <span class="material-icons-outlined ai-visibility-run-progress__pill-icon">hourglass_bottom</span>
      <span>Hang tight! Your AI tracker is on the way - this can take up to 2 minute per question...</span>
    </template>

    <template v-else-if="displayIsComplete">
      <div class="ai-visibility-run-progress__title">
        <span>AI tracker already loaded</span>
        <span class="material-icons ai-visibility-run-progress__complete-icon">check_circle</span>
      </div>
      <p>Your AI tracker loaded, please check it.</p>
      <button
        type="button"
        class="ai-visibility-run-progress__button"
        @click="handleOpen"
      >
        Your AI Visibility report is ready
      </button>
    </template>

    <template v-else>
      <div class="ai-visibility-run-progress__title">
        <span>Hang tight!</span>
        <span class="material-icons-outlined ai-visibility-run-progress__running-icon">hourglass_bottom</span>
      </div>
      <p>Your AI tracker is on the way - this can take up to 2 minute per question</p>
      <div
        class="ai-visibility-run-progress__bar"
        role="progressbar"
        :aria-valuenow="normalizedPercentage"
        aria-valuemin="0"
        aria-valuemax="100"
      >
        <div
          class="ai-visibility-run-progress__bar-fill"
          :style="{ width: `${normalizedPercentage}%` }"
        ></div>
      </div>
    </template>
  </div>
</template>

<script>
import { getAiVisibilityProgress, getAiVisibilityTrackers } from '../api'
import { EventBus } from '@/utils'
import {
  AI_VISIBILITY_RUN_PROGRESS_COMPLETED,
  AI_VISIBILITY_RUN_PROGRESS_STARTED,
} from './events'

const PROGRESS_POLL_INTERVAL_MS = 5000
const PROGRESS_STORAGE_KEY = 'aiVisibilityRunProgressTracker'

export default {
  name: 'AiVisibilityRunProgressPopup',
  props: {
    selfContained: {
      type: Boolean,
      default: false,
    },
    percentage: {
      type: Number,
      default: 0,
    },
    isComplete: {
      type: Boolean,
      default: false,
    },
    variant: {
      type: String,
      default: 'card',
      validator (value) {
        return ['card', 'pill'].includes(value)
      },
    },
  },
  data () {
    return {
      progressTracker: null,
      progressSiteId: null,
      internalProgress: null,
      progressTimer: null,
      progressRequestInFlight: false,
      progressStartedFromCreation: false,
      routeTrackerLookupInFlight: false,
    }
  },
  computed: {
    shouldRender () {
      if (!this.selfContained) {
        return true
      }
      return this.showProgressPopup
    },
    displayVariant () {
      if (!this.selfContained) {
        return this.variant
      }
      if (this.isCurrentRouteTracker && !this.displayIsComplete) {
        return 'pill'
      }
      return 'card'
    },
    displayIsComplete () {
      if (!this.selfContained) {
        return this.isComplete
      }
      return Boolean(this.internalProgress && this.internalProgress.isComplete)
    },
    displayPercentage () {
      if (!this.selfContained) {
        return this.percentage
      }
      return this.internalProgress ? this.internalProgress.percentage : 0
    },
    normalizedPercentage () {
      if (!Number.isFinite(this.displayPercentage)) {
        return 0
      }
      return Math.max(0, Math.min(100, Math.round(this.displayPercentage)))
    },
    showProgressPopup () {
      return Boolean(
        this.progressTracker &&
        this.internalProgress &&
        (
          this.progressStartedFromCreation ||
          this.isFirstRunProgress(this.internalProgress)
        ),
      )
    },
    isCurrentRouteTracker () {
      return Boolean(
        this.progressSiteId &&
        this.progressTracker &&
        this.$route &&
        Number(this.$route.params.siteId) === Number(this.progressSiteId) &&
        Number(this.$route.params.trackerId) === Number(this.progressTracker.id),
      )
    },
  },
  watch: {
    '$route.params.siteId' () {
      this.loadRunningTrackerForRouteSite()
    },
    '$store.state.selectedSubAccountRelation' () {
      this.stopProgressPolling()
      this.loadRunningTrackerForRouteSite()
    },
  },
  mounted () {
    if (!this.selfContained) {
      return
    }

    EventBus.$on(AI_VISIBILITY_RUN_PROGRESS_STARTED, this.handleProgressStarted)
    this.restoreProgressPolling()
    this.loadRunningTrackerForRouteSite()
  },
  beforeDestroy () {
    if (!this.selfContained) {
      return
    }

    EventBus.$off(AI_VISIBILITY_RUN_PROGRESS_STARTED, this.handleProgressStarted)
    this.clearProgressTimer()
  },
  methods: {
    handleOpen () {
      if (!this.selfContained) {
        this.$emit('open')
        return
      }

      if (!this.progressTracker || this.progressTracker.id == null || !this.progressSiteId) {
        return
      }

      const siteId = String(this.progressSiteId)
      const trackerId = String(this.progressTracker.id)
      this.stopProgressPolling()
      this.$router.push({
        name: 'AiVisibilityOverview',
        params: { siteId, trackerId },
      })
    },
    handleProgressStarted ({ siteId, tracker, fromCreation = false } = {}) {
      this.startProgressPolling(tracker, {
        siteId,
        fromCreation,
        waitForInitialProgress: !fromCreation,
      })
    },
    startProgressPolling (tracker, options = {}) {
      const siteId = Number(options.siteId || tracker?.siteId)
      if (!this.selfContained || !tracker || tracker.id == null || !siteId) {
        return
      }

      this.clearProgressTimer()
      this.progressTracker = tracker
      this.progressSiteId = siteId
      this.progressStartedFromCreation = Boolean(options.fromCreation)
      this.internalProgress = options.waitForInitialProgress
        ? null
        : {
            percentage: 0,
            isComplete: false,
            hasReadyRun: false,
          }
      this.persistProgressPolling()
      this.pollProgress()
      this.progressTimer = setInterval(
        this.pollProgress,
        PROGRESS_POLL_INTERVAL_MS,
      )
    },
    pollProgress () {
      if (
        this.progressRequestInFlight ||
        !this.progressTracker ||
        this.progressTracker.id == null ||
        !this.progressSiteId
      ) {
        return Promise.resolve()
      }

      this.progressRequestInFlight = true
      return getAiVisibilityProgress(this.progressSiteId, this.progressTracker.id)
        .then((response) => {
          this.internalProgress = {
            percentage: 0,
            isComplete: false,
            hasReadyRun: false,
            ...(response.data || {}),
          }
          if (
            !this.progressStartedFromCreation &&
            !this.isFirstRunProgress(this.internalProgress)
          ) {
            this.stopProgressPolling()
            return
          }
          if (this.internalProgress.isComplete) {
            this.clearProgressTimer()
            this.clearPersistedProgress()
            EventBus.$emit(AI_VISIBILITY_RUN_PROGRESS_COMPLETED, {
              siteId: this.progressSiteId,
              tracker: this.progressTracker,
              progress: this.internalProgress,
            })
          }
        })
        .catch(() => {
          this.stopProgressPolling()
        })
        .finally(() => {
          this.progressRequestInFlight = false
        })
    },
    loadRunningTrackerForRouteSite () {
      const siteId = Number(this.$route && this.$route.params.siteId)
      if (
        !this.selfContained ||
        !siteId ||
        this.progressTracker ||
        this.routeTrackerLookupInFlight
      ) {
        return Promise.resolve()
      }

      this.routeTrackerLookupInFlight = true
      return getAiVisibilityTrackers(siteId)
        .then((response) => {
          const trackers = response.data.trackers || []
          const runningTracker = trackers.find((tracker) => tracker.isRunning)
          if (runningTracker) {
            this.startProgressPolling(runningTracker, {
              siteId,
              waitForInitialProgress: true,
            })
          }
        })
        .catch(() => {
          this.clearPersistedProgress()
        })
        .finally(() => {
          this.routeTrackerLookupInFlight = false
        })
    },
    restoreProgressPolling () {
      const storedProgress = this.getPersistedProgress()
      if (!storedProgress) {
        return
      }

      this.startProgressPolling(storedProgress.tracker, {
        siteId: storedProgress.siteId,
        fromCreation: storedProgress.fromCreation,
        waitForInitialProgress: true,
      })
    },
    persistProgressPolling () {
      try {
        localStorage.setItem(PROGRESS_STORAGE_KEY, JSON.stringify({
          siteId: this.progressSiteId,
          tracker: this.progressTracker,
          fromCreation: this.progressStartedFromCreation,
        }))
      } catch {
        // Ignore storage failures; in-memory polling still keeps the popup alive.
      }
    },
    getPersistedProgress () {
      try {
        return JSON.parse(localStorage.getItem(PROGRESS_STORAGE_KEY))
      } catch {
        return null
      }
    },
    clearPersistedProgress () {
      try {
        localStorage.removeItem(PROGRESS_STORAGE_KEY)
      } catch {
        // Ignore storage failures; polling state can still clear in memory.
      }
    },
    clearProgressTimer () {
      if (this.progressTimer) {
        clearInterval(this.progressTimer)
        this.progressTimer = null
      }
    },
    stopProgressPolling () {
      this.clearProgressTimer()
      this.clearPersistedProgress()
      this.progressTracker = null
      this.progressSiteId = null
      this.internalProgress = null
      this.progressRequestInFlight = false
      this.progressStartedFromCreation = false
    },
    isFirstRunProgress (progress) {
      return Boolean(progress && progress.hasReadyRun === false)
    },
  },
}
</script>

<style lang="scss" scoped>

.ai-visibility-run-progress {
  position: fixed;
  z-index: 30;
  color: var(--v-mainColor-base);
  font-family: "Mulish", sans-serif;
}

.ai-visibility-run-progress--card {
  right: 24px;
  bottom: 24px;
  display: flex;
  width: 370px;
  max-width: calc(100vw - 32px);
  flex-direction: column;
  gap: 6px;
  border: 1px solid var(--v-gray12Color-base);
  border-radius: 8px;
  background: var(--v-whiteColor-base);
  box-shadow: 0 4px 18px rgba(0, 0, 0, 0.2);
  padding: 14px;
}

.ai-visibility-run-progress--pill {
  left: 50%;
  bottom: 20px;
  display: flex;
  max-width: calc(100vw - 28px);
  align-items: center;
  justify-content: center;
  gap: 16px;
  border-radius: 30px;
  background: var(--v-profileAnchorColor-base);
  color: var(--v-whiteColor-base);
  font-size: 20px;
  font-weight: 500;
  line-height: 1.2;
  padding: 20px 32px;
  transform: translateX(-50%);
  white-space: nowrap;
}

.ai-visibility-run-progress__title {
  display: flex;
  align-items: center;
  gap: 10px;
  font-size: 18px;
  font-weight: 500;
  line-height: 1.2;

  span {
    font-weight: 500;
    font-size: 18px;
  }
}

.ai-visibility-run-progress__running-icon {
  color: var(--v-profileAnchorColor-base);
  font-size: 24px;
  animation: hourglassFlip 2s infinite ease-in-out;
}

.ai-visibility-run-progress__complete-icon {
  color: var(--v-green7Color-base);
  font-size: 24px;
}

.ai-visibility-run-progress__pill-icon {
  flex: 0 0 auto;
  color: var(--v-whiteColor-base);
  font-size: 24px;
  animation: hourglassFlip 2s infinite ease-in-out;
}

.ai-visibility-run-progress p {
  color: var(--v-mainColor-base);
  font-size: 16px;
  line-height: 1.5;
  margin: 0;
}

.ai-visibility-run-progress__bar {
  width: 100%;
  height: 14px;
  overflow: hidden;
  border-radius: 30px;
  background: rgba(255, 87, 0, 0.2);
}

.ai-visibility-run-progress__bar-fill {
  height: 100%;
  border-radius: 30px;
  background: var(--v-profileAnchorColor-base);
  transition: width 0.2s ease;
}

.ai-visibility-run-progress__button {
  width: 100%;
  min-height: 42px;
  border: 0;
  border-radius: 30px;
  background: var(--v-profileAnchorColor-base);
  color: var(--v-whiteColor-base);
  cursor: pointer;
  font: inherit;
  font-size: 16px;
  line-height: 1.5;
  padding: 10px 20px;
}

.ai-visibility-run-progress__button:focus-visible {
  outline: 2px solid var(--v-mainColor-base);
  outline-offset: 2px;
}

@media (max-width: 720px) {
  .ai-visibility-run-progress--card {
    right: 16px;
    bottom: 16px;
    left: 16px;
    width: auto;
    max-width: none;
  }

  .ai-visibility-run-progress--pill {
    width: calc(100vw - 28px);
    white-space: normal;
    font-size: 16px;
    padding: 16px 22px;
  }
}

@keyframes hourglassFlip {
  0% {
    transform: rotate(0deg);
  }
  /* Holds still at 0 degrees for the first half of the cycle */
  45% {
    transform: rotate(0deg);
  }
  /* Quickly flips upside down */
  55% {
    transform: rotate(180deg);
  }
  /* Holds still upside down to complete the cycle */
  100% {
    transform: rotate(180deg);
  }
}
</style>
